import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BASE = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password minimal 6 karakter'); return; }
    if (password !== confirm) { toast.error('Password tidak sama'); return; }
    if (!token) { toast.error('Token tidak valid'); return; }

    setLoading(true);
    setErrorMsg('');
    try {
      await axios.post(`${BASE}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Gagal reset password';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8 w-full max-w-md text-center">
          <XCircle size={40} className="text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800">Link Tidak Valid</h1>
          <p className="text-slate-500 text-sm mt-2">Token reset password tidak ditemukan.</p>
          <Link to="/forgot-password" className="inline-block mt-4 text-sm text-[#0f6c44] font-semibold hover:underline">
            Minta link baru
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
          {done ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto">
                <CheckCircle2 size={32} className="text-green-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-800">Password Berhasil Direset!</h1>
              <p className="text-slate-500 text-sm">Kamu akan diarahkan ke halaman login dalam beberapa detik...</p>
              <Link to="/login" className="inline-block text-sm text-[#0f6c44] font-semibold hover:underline">
                Login sekarang
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
                  <Lock size={22} className="text-[#0f6c44]" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800 font-manrope">Buat Password Baru</h1>
                <p className="text-slate-500 text-sm mt-1">Masukkan password baru untuk akunmu.</p>
              </div>

              {errorMsg && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
                  <XCircle size={16} className="text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{errorMsg}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1.5">Password Baru</label>
                  <div className="relative">
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Minimal 6 karakter"
                      data-testid="reset-password-input"
                      className="w-full px-4 py-3 pr-10 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44] bg-slate-50"
                    />
                    <button type="button" onClick={() => setShowPw(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1.5">Konfirmasi Password</label>
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Ulangi password baru"
                    data-testid="reset-confirm-input"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44] bg-slate-50"
                  />
                </div>
                <button type="submit" disabled={loading} data-testid="reset-submit-btn"
                  className="w-full py-3 bg-[#0f6c44] text-white rounded-xl font-semibold text-sm hover:bg-[#0c5636] active:scale-95 transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                  {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : null}
                  {loading ? 'Menyimpan...' : 'Simpan Password Baru'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
